import styled from 'styled-components';
import { FormBuilderInputType, FormItem } from './formBuilder.types';
import { Typography } from '@mui/material';

const gapPx = 20;

export const FormBuilderGroup = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: flex-start;
  gap: ${gapPx}px;
  flex-grow: 1;
  flex-wrap: wrap;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const FormBuilderItem = styled.div`
  margin: 8px 0;
  flex-grow: 1;

  @media screen and (max-width: 768px) {
    width: 100%;
    margin: 10px 0;
  }
`;

export const FormBuilderLabel = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <FormBuilderGroup>
      <Typography
        sx={{
          fontFamily: `'Montserrat', 'Lato', 'sans-serif'`,
          marginBottom: '-11px',
        }}
        variant="titleMedium"
      >
        {children}
      </Typography>
    </FormBuilderGroup>
  );
};

const defaultMinWidth = 130;
const minWidth: Record<FormBuilderInputType, number> = {
  text: defaultMinWidth,
  number: defaultMinWidth,
  phone: defaultMinWidth,
  checkbox: defaultMinWidth,
  select: defaultMinWidth,
  autocomplete: defaultMinWidth,
  radioGroup: defaultMinWidth,
  date: defaultMinWidth,
  multiSelect: 0,
  checkboxGroup: 0,
  richText: defaultMinWidth,
  searchBar: defaultMinWidth,
};

const getItemMinWidthTotal = <T,>(formItem: FormItem<T>): number => {
  if (Array.isArray(formItem)) {
    formItem = { type: 'group', items: formItem };
  }
  if (formItem.type === 'group') {
    return (
      formItem.items.reduce((acc, item) => {
        return acc + getItemMinWidthTotal(item);
      }, 0) +
      formItem.items.length * gapPx
    ); // Add gap between items
  } else if (formItem.type === 'input') {
    return formItem.formLayoutMinWidth ?? minWidth[formItem.input];
  } else if (formItem.type === 'passthrough') {
    return formItem.minWidth || 0;
  }
};

export const getFormBuilderItemMinWidth = <T,>(
  formItem: FormItem<T>,
  formWidth: number
): number => {
  const minWidth = getItemMinWidthTotal(formItem);
  return minWidth > formWidth ? formWidth : minWidth;
};

export const getFlexBasis = <T,>(
  formItem: FormItem<T>,
  siblingCount: number
): string => {
  if (Array.isArray(formItem)) {
    formItem = { type: 'group', items: formItem };
  }
  const gapCount = Math.max(siblingCount - 1, 0);
  return `calc(${formItem.size || 100 / siblingCount}% - ${
    gapCount * (gapPx / 2)
  }px)`;
};
